import { getMediaType } from '../../widget/inputs/FieldMedia';

/** Familles de médias qu'un dépôt de fichier peut désigner. */
export type MediaDropKind = 'video' | 'audio' | 'image';

/** Libellé du type, tel que MediaSheet le nomme (mediaToCreateTypeNames). */
export const MEDIA_KIND_TYPE_NAME: Record<MediaDropKind, string> = {
  video: 'Vidéo',
  audio: 'Son',
  image: 'Image',
};

/**
 * Le type que déclare le navigateur fait foi ; l'extension ne sert que lorsqu'il est
 * vide (un .mkv arrive souvent sans type). C'est la règle de FieldSound, reprise ici
 * pour que le dépôt et le champ qui reçoit ensuite le fichier ne soient jamais en désaccord.
 */
export const detectDroppedMediaKind = (file: File): MediaDropKind | null => {
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type.startsWith('image/')) return 'image';

  const byName = getMediaType(file.name);
  if (byName === 'Video') return 'video';
  if (byName === 'Sound') return 'audio';
  if (byName === 'Image') return 'image';
  return null;
};

/**
 * Remet à la fiche le fichier déjà choisi, comme si l'utilisateur l'avait pris dans
 * le sélecteur du champ : même événement `change`, donc même chemin de code (aperçu,
 * durée de la vidéo, validation, compteur de modifications). Aucune fiche n'a besoin
 * de connaître ce mécanisme.
 *
 * Le bon champ se reconnaît à son `accept` : la fiche vidéo en porte deux (vignette
 * et vidéo). Faux tant que le champ n'est pas encore monté — l'appelant réessaie.
 */
export const injectFileIntoSheet = (root: HTMLElement, file: File, kind: MediaDropKind): boolean => {
  const input = root.querySelector<HTMLInputElement>(`input[type="file"][accept^="${kind}/"]`);
  if (!input || input.disabled) return false;

  const transfer = new DataTransfer();
  transfer.items.add(file);
  input.files = transfer.files;
  input.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} Go`;
  if (bytes >= 1024 * 1024)        return `${Math.round(bytes / (1024 * 1024))} Mo`;
  return `${Math.max(1, Math.round(bytes / 1024))} Ko`;
};
